import { useState, useEffect, useCallback, useRef } from 'react';
import QuizService from '../../services/quizService';
import type { QuizQuestion } from '../../services/quizService';
import toast from '../../utils/toast';

interface QuizAdminPanelProps {
  onClose: () => void;
}

export default function QuizAdminPanel({ onClose }: QuizAdminPanelProps) {
  const quizServiceRef = useRef(QuizService.getInstance());
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('all');
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<QuizQuestion | null>(null);
  const [newQuestion, setNewQuestion] = useState<Omit<QuizQuestion, 'id' | 'createdAt' | 'updatedAt'>>({
    question: '',
    options: ['', '', '', ''],
    correctAnswer: 0,
    explanation: '',
    category: 'basic_principles',
    difficulty: 'easy',
    active: true,
  });

  const loadQuestions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await quizServiceRef.current.getAllQuestions();
      setQuestions(data);
    } catch {
      toast.error('Erro ao carregar perguntas. Verifique o console.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadQuestions();
  }, [loadQuestions]);

  const filteredQuestions = questions.filter(q => {
    const categoryMatch = selectedCategory === 'all' || q.category === selectedCategory;
    const difficultyMatch = selectedDifficulty === 'all' || q.difficulty === selectedDifficulty;
    return categoryMatch && difficultyMatch;
  });

  const handleAddQuestion = async () => {
    if (newQuestion.question && newQuestion.options.every((opt: string) => opt.trim()) && newQuestion.explanation) {
      try {
        setLoading(true);
        const quizService = quizServiceRef.current;
        await quizService.addQuestion(newQuestion);
        await loadQuestions();
        resetForm();
        setShowAddModal(false);
        toast.success('Pergunta adicionada com sucesso!');
      } catch (error) {
        console.error('Erro ao adicionar pergunta:', error);
        toast.error('Erro ao adicionar pergunta. Verifique o console.');
      } finally {
        setLoading(false);
      }
    } else {
      toast.warning('Preencha todos os campos obrigatórios!');
    }
  };

  const handleEditQuestion = async () => {
    if (editingQuestion && editingQuestion.question && editingQuestion.options.every((opt: string) => opt.trim()) && editingQuestion.explanation) {
      try {
        setLoading(true);
        const { id, ...updates } = editingQuestion;
        const quizService = quizServiceRef.current;
        await quizService.updateQuestion(id, updates);
        await loadQuestions();
        setEditingQuestion(null);
        setShowAddModal(false);
        toast.success('Pergunta atualizada com sucesso!');
      } catch (error) {
        console.error('Erro ao atualizar pergunta:', error);
        toast.error('Erro ao atualizar pergunta. Verifique o console.');
      } finally {
        setLoading(false);
      }
    } else {
      toast.warning('Preencha todos os campos obrigatórios!');
    }
  };

  const handleDeleteQuestion = async (id: string) => {
    if (confirm('Tem certeza que deseja excluir esta pergunta?')) {
      try {
        setLoading(true);
        const quizService = quizServiceRef.current;
        await quizService.deleteQuestion(id);
        await loadQuestions();
        toast.success('Pergunta excluída com sucesso!');
      } catch (error) {
        console.error('Erro ao excluir pergunta:', error);
        toast.error('Erro ao excluir pergunta. Verifique o console.');
      } finally {
        setLoading(false);
      }
    }
  };

  const toggleQuestionStatus = async (id: string) => {
    const question = questions.find(q => q.id === id);
    if (question) {
      try {
        setLoading(true);
        const quizService = quizServiceRef.current;
        await quizService.updateQuestion(id, { active: !question.active });
        await loadQuestions();
      } catch (error) {
        console.error('Erro ao alterar status:', error);
        toast.error('Erro ao alterar status. Verifique o console.');
      } finally {
        setLoading(false);
      }
    }
  };

  const resetForm = () => {
    setNewQuestion({
      question: '',
      options: ['', '', '', ''],
      correctAnswer: 0,
      explanation: '',
      category: 'basic_principles',
      difficulty: 'easy',
      active: true
    });
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      basic_principles: 'Princípios Básicos',
      remedies: 'Medicamentos',
      potencies: 'Potências',
      history: 'História'
    };
    return labels[category] || category;
  };

  const getDifficultyLabel = (difficulty: string) => {
    const labels: Record<string, string> = {
      easy: 'Fácil',
      medium: 'Médio',
      hard: 'Difícil'
    };
    return labels[difficulty] || difficulty;
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return 'bg-green-100 text-green-600';
      case 'medium':
        return 'bg-yellow-100 text-yellow-600';
      case 'hard':
        return 'bg-red-100 text-red-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'basic_principles':
        return 'bg-blue-100 text-blue-600';
      case 'remedies':
        return 'bg-green-100 text-green-600';
      case 'potencies':
        return 'bg-purple-100 text-purple-600';
      case 'history':
        return 'bg-orange-100 text-orange-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const currentQuestion = editingQuestion || newQuestion;
  const isEditing = !!editingQuestion;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold text-gray-800">Gerenciar Quiz de Homeopatia</h3>
              <p className="text-sm text-gray-600 mt-1">
                {filteredQuestions.length} pergunta{filteredQuestions.length !== 1 ? 's' : ''} 
                {selectedCategory !== 'all' || selectedDifficulty !== 'all' ? ' (filtradas)' : ''}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  resetForm();
                  setEditingQuestion(null);
                  setShowAddModal(true);
                }}
                className="px-4 py-2 bg-indigo-500 text-white rounded-xl hover:bg-indigo-600 transition-colors flex items-center gap-2"
              >
                <i className="ri-add-line"></i>
                Nova Pergunta
              </button>
              <button
                onClick={onClose}
                className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
              >
                <i className="ri-close-line text-gray-600"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="p-6 border-b border-gray-100 flex-shrink-0">
          <div className="flex gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Categoria</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:border-indigo-300 focus:outline-none"
              >
                <option value="all">Todas as categorias</option>
                <option value="basic_principles">Princípios Básicos</option>
                <option value="remedies">Medicamentos</option>
                <option value="potencies">Potências</option>
                <option value="history">História</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Dificuldade</label>
              <select
                value={selectedDifficulty}
                onChange={(e) => setSelectedDifficulty(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:border-indigo-300 focus:outline-none"
              >
                <option value="all">Todas as dificuldades</option>
                <option value="easy">Fácil</option>
                <option value="medium">Médio</option>
                <option value="hard">Difícil</option>
              </select>
            </div>
          </div>
        </div>

        {/* Lista de Perguntas */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            {filteredQuestions.map((question) => (
              <div key={question.id} className="border border-gray-200 rounded-2xl p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0 mr-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(question.category)}`}>
                        {getCategoryLabel(question.category)}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(question.difficulty)}`}>
                        {getDifficultyLabel(question.difficulty)}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        question.active 
                          ? 'bg-green-100 text-green-600' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {question.active ? 'Ativa' : 'Inativa'}
                      </span>
                    </div>
                    <h4 className="font-semibold text-gray-800 mb-2">{question.question}</h4>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      {question.options.map((option: string, index: number) => (
                        <div key={index} className={`text-sm p-2 rounded-lg ${
                          index === question.correctAnswer 
                            ? 'bg-green-50 text-green-700 border border-green-200' 
                            : 'bg-gray-50 text-gray-600'
                        }`}>
                          <span className="font-medium">{String.fromCharCode(65 + index)})</span> {option}
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-gray-600 italic">{question.explanation}</p>
                  </div>
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <button
                      onClick={() => toggleQuestionStatus(question.id)}
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                        question.active 
                          ? 'bg-green-100 text-green-600' 
                          : 'bg-gray-100 text-gray-600'
                      }`}
                      title={question.active ? 'Desativar' : 'Ativar'}
                    >
                      <i className={question.active ? 'ri-eye-line' : 'ri-eye-off-line'}></i>
                    </button>
                    <button
                      onClick={() => {
                        setEditingQuestion(question);
                        setShowAddModal(true);
                      }}
                      className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm"
                    >
                      <i className="ri-edit-line"></i>
                    </button>
                    <button
                      onClick={() => handleDeleteQuestion(question.id)}
                      className="w-8 h-8 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-sm"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {filteredQuestions.length === 0 && (
              <div className="text-center py-12">
                <i className="ri-question-line text-4xl text-gray-300 mb-4"></i>
                <p className="text-gray-500">Nenhuma pergunta encontrada</p>
              </div>
            )}
          </div>
        </div>

        {/* Modal Adicionar/Editar Pergunta */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 z-60 flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold text-gray-800">
                    {isEditing ? 'Editar Pergunta' : 'Nova Pergunta'}
                  </h4>
                  <button
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingQuestion(null);
                      resetForm();
                    }}
                    className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center"
                  >
                    <i className="ri-close-line text-gray-600"></i>
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                {/* Pergunta */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pergunta</label>
                  <textarea
                    value={currentQuestion.question}
                    onChange={(e) => {
                      if (isEditing) {
                        setEditingQuestion({ ...editingQuestion!, question: e.target.value });
                      } else {
                        setNewQuestion({ ...newQuestion, question: e.target.value });
                      }
                    }}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-indigo-300 focus:outline-none resize-none"
                    placeholder="Digite a pergunta do quiz"
                  />
                </div>

                {/* Opções */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Opções de Resposta</label>
                  <div className="space-y-2">
                    {currentQuestion.options.map((option: string, index: number) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                          index === currentQuestion.correctAnswer 
                            ? 'bg-green-100 text-green-600' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {String.fromCharCode(65 + index)}
                        </div>
                        <input
                          type="text"
                          value={option}
                          onChange={(e) => {
                            const newOptions = [...currentQuestion.options];
                            newOptions[index] = e.target.value;
                            if (isEditing) {
                              setEditingQuestion({ ...editingQuestion!, options: newOptions });
                            } else {
                              setNewQuestion({ ...newQuestion, options: newOptions });
                            }
                          }}
                          className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-sm focus:border-indigo-300 focus:outline-none"
                          placeholder={`Opção ${String.fromCharCode(65 + index)}`}
                        />
                        <button
                          onClick={() => {
                            if (isEditing) {
                              setEditingQuestion({ ...editingQuestion!, correctAnswer: index });
                            } else {
                              setNewQuestion({ ...newQuestion, correctAnswer: index });
                            }
                          }}
                          className={`px-3 py-2 rounded-xl text-xs font-medium transition-colors ${
                            index === currentQuestion.correctAnswer
                              ? 'bg-green-500 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {index === currentQuestion.correctAnswer ? 'Correta' : 'Marcar'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Explicação */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Explicação</label>
                  <textarea
                    value={currentQuestion.explanation}
                    onChange={(e) => {
                      if (isEditing) {
                        setEditingQuestion({ ...editingQuestion!, explanation: e.target.value });
                      } else {
                        setNewQuestion({ ...newQuestion, explanation: e.target.value });
                      }
                    }}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-indigo-300 focus:outline-none resize-none"
                    placeholder="Explicação educativa sobre a resposta correta"
                  />
                </div>

                {/* Categoria e Dificuldade */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Categoria</label>
                    <select
                      value={currentQuestion.category}
                      onChange={(e) => {
                        const category = e.target.value as QuizQuestion['category'];
                        if (isEditing) {
                          setEditingQuestion({ ...editingQuestion!, category });
                        } else {
                          setNewQuestion({ ...newQuestion, category });
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:border-indigo-300 focus:outline-none"
                    >
                      <option value="basic_principles">Princípios Básicos</option>
                      <option value="remedies">Medicamentos</option>
                      <option value="potencies">Potências</option>
                      <option value="history">História</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Dificuldade</label>
                    <select
                      value={currentQuestion.difficulty}
                      onChange={(e) => {
                        const difficulty = e.target.value as QuizQuestion['difficulty'];
                        if (isEditing) {
                          setEditingQuestion({ ...editingQuestion!, difficulty });
                        } else {
                          setNewQuestion({ ...newQuestion, difficulty });
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:border-indigo-300 focus:outline-none"
                    >
                      <option value="easy">Fácil</option>
                      <option value="medium">Médio</option>
                      <option value="hard">Difícil</option>
                    </select>
                  </div>
                </div>

                {/* Status Ativo */}
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Pergunta Ativa</label>
                  <button
                    onClick={() => {
                      if (isEditing) {
                        setEditingQuestion({ ...editingQuestion!, active: !editingQuestion!.active });
                      } else {
                        setNewQuestion({ ...newQuestion, active: !newQuestion.active });
                      }
                    }}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      currentQuestion.active ? 'bg-indigo-500' : 'bg-gray-300'
                    }`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                      currentQuestion.active ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>

              {/* Botões */}
              <div className="sticky bottom-0 bg-white p-6 border-t border-gray-100 rounded-b-3xl">
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingQuestion(null);
                      resetForm();
                    }}
                    className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-2xl font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={isEditing ? handleEditQuestion : handleAddQuestion}
                    disabled={loading}
                    className="flex-1 py-3 bg-indigo-500 text-white rounded-2xl font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Salvando...' : (isEditing ? 'Salvar' : 'Adicionar')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}